import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, StatCard, Surface, Empty } from "../components/Primitives";
import PublicCatalogWidget from "../components/PublicCatalogWidget";
import { Package, Warehouse, Truck, Banknote, AlertTriangle, CalendarClock, TrendingUp, ShoppingCart, BookOpen } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/dashboard/stats"); setStats(data); })(); }, []);
  if (!stats) return <div className="text-[var(--text-muted)] font-data text-sm">Chargement...</div>;

  // Format currency with em-dash when zero to avoid "OF" visual confusion
  const fmtMoney = (v) => (v && v > 0 ? `${v.toLocaleString("fr-FR")} F` : "—");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tableau de bord"
        subtitle="Vue d'ensemble · Temps réel"
        testId="dashboard-header"
        actions={
          <a
            data-testid="download-guide"
            href="/guide-utilisateur.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            className="btn-ghost flex items-center gap-2 text-[var(--primary-dark,#8E3A6D)] border-[var(--primary,#C2569B)]/40 hover:bg-[var(--primary-soft,#F8E8F1)]"
          >
            <BookOpen size={14}/> Guide utilisateur (PDF)
          </a>
        }
      />

      <PublicCatalogWidget />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="stats-grid">
        <StatCard label="Produits" value={stats.counts.products} icon={Package} testId="stat-products" />
        <StatCard label="Sites" value={stats.counts.sites} icon={Warehouse} testId="stat-sites" />
        <StatCard label="Fournisseurs" value={stats.counts.suppliers} icon={Truck} testId="stat-suppliers" />
        <StatCard label="Stock total (unités)" value={stats.counts.total_stock_units} accent="green" icon={Package} testId="stat-stock" />
        <StatCard label="Chiffre d'affaires" value={fmtMoney(stats.revenue.total)} accent="cyan" icon={Banknote} testId="stat-revenue" />
        <StatCard label="Encaissé" value={fmtMoney(stats.revenue.paid)} accent="green" icon={TrendingUp} testId="stat-paid" />
        <StatCard label="Reliquat" value={fmtMoney(stats.revenue.balance)} accent="pink" icon={AlertTriangle} testId="stat-balance" />
        <StatCard label="Ventes du jour" value={fmtMoney(stats.revenue.today)} accent="cyan" icon={ShoppingCart} testId="stat-today" />
      </div>

      <Surface title="Ventes — 30 derniers jours" testId="sales-chart">
        <div className="p-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.sales_chart} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C2569B" stopOpacity={0.45}/>
                  <stop offset="100%" stopColor="#C2569B" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 5" stroke="#E2E7E4" vertical={false}/>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#8B939B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
              <YAxis stroke="#8B939B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}/>
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E7E4", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#5C6670" }} cursor={{ stroke: "#C2569B", strokeDasharray: "3 3" }}/>
              <Area type="monotone" dataKey="total" stroke="#C2569B" strokeWidth={2.5} fill="url(#grad)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Surface>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Surface title="Alertes stock bas" testId="low-stock-panel">
          {stats.low_stock.length === 0 ? <Empty message="Aucune alerte" /> : (
            <table className="data-table">
              <thead><tr><th>Produit</th><th>Stock</th><th>Seuil</th><th></th></tr></thead>
              <tbody>{stats.low_stock.map((s) => (
                <tr key={s.product_id}><td>{s.name}</td><td className="font-data text-[var(--neon-pink)]">{s.qty}</td><td className="font-data text-[var(--text-muted)]">{s.min_stock}</td><td><span className="badge-alert">CRITIQUE</span></td></tr>
              ))}</tbody>
            </table>
          )}
        </Surface>

        <Surface title="Lots à péremption proche" testId="expiring-panel">
          {stats.expiring.length === 0 ? <Empty message="Aucun lot critique" /> : (
            <table className="data-table">
              <thead><tr><th>Produit</th><th>Lot</th><th>Date</th><th>Qté</th><th></th></tr></thead>
              <tbody>{stats.expiring.map((b) => (
                <tr key={b.batch_id}><td>{b.product_name}</td><td className="font-data">{b.batch_number}</td><td className="font-data text-[var(--text-muted)]">{b.expiry_date}</td><td className="font-data">{b.quantity}</td><td>{b.expired ? <span className="badge-alert">EXPIRÉ</span> : <span className="badge-warn">PROCHE</span>}</td></tr>
              ))}</tbody>
            </table>
          )}
        </Surface>
      </div>

      <Surface title="Ventes récentes" testId="recent-sales-panel" action={<span className="label-mono flex items-center gap-2"><CalendarClock size={12} />5 dernières</span>}>
        {stats.recent_sales.length === 0 ? <Empty message="Aucune vente enregistrée" /> : (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Client</th><th>Total</th><th>Encaissé</th><th>Reliquat</th><th>Statut</th></tr></thead>
            <tbody>{stats.recent_sales.map((s) => (
              <tr key={s.id}>
                <td className="font-data text-[var(--text-muted)]">{s.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td>{s.customer_name}</td>
                <td className="font-data">{s.total.toLocaleString("fr-FR")} F</td>
                <td className="font-data neon-text-green">{s.paid.toLocaleString("fr-FR")} F</td>
                <td className="font-data neon-text-pink">{s.balance.toLocaleString("fr-FR")} F</td>
                <td><span className={s.status === "payée" ? "badge-success" : s.status === "partielle" ? "badge-warn" : "badge-alert"}>{s.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
